/**
 * Web ads through the H5 Games Ads (Ad Placement) API: adsbygoogle.js with
 * adBreak()/adConfig(). Skeleton; the script loads on init() only.
 * Note: H5 rewarded completion is client-attested (no SSV), so the API must be
 * configured to accept `network: 'h5'` completions for web.
 */
import { loadScript } from '../lazy.js';
import { nonPersonalized, type AdSlot, type AdsInitOptions, type AdsProvider, type RewardedResult } from './types.js';

type BreakStatus = 'notReady' | 'timeout' | 'error' | 'noAdPreloaded' | 'frequencyCapped' | 'ignored' | 'other' | 'dismissed' | 'viewed' | 'invalid';

interface AdBreak {
  type: 'reward' | 'next' | 'start' | 'pause' | 'browse';
  name: string;
  beforeAd?(): void;
  afterAd?(): void;
  beforeReward?(showAdFn: () => void): void;
  adDismissed?(): void;
  adViewed?(): void;
  adBreakDone?(info: { breakStatus: BreakStatus }): void;
}
interface AdConfig {
  preloadAdBreaks?: 'on' | 'auto';
  sound?: 'on' | 'off';
  onReady?(): void;
}
type AdsByGoogle = (AdBreak | AdConfig)[] & { requestNonPersonalizedAds?: 0 | 1 };

export interface H5AdsConfig {
  /** AdSense publisher id, ca-pub-… */
  client: string;
  /** adds data-adbreak-test="on" */
  testMode?: boolean;
  /** pause/mute the game around an ad */
  onPause?: () => void;
  onResume?: () => void;
  document?: Document;
}

/** An ad break that has not started (beforeReward or beforeAd) by now is a no-fill: the real tag answers at once. */
export const H5_START_MS = 5_000;
/** The longest a started ad break may hold the screen before the game takes it back. */
export const H5_MAX_BREAK_MS = 120_000;

export class H5GamesAdsProvider implements AdsProvider {
  readonly network = 'h5';
  private ready = false;
  /** init() could not load the tag (an ad blocker, no network): nothing will ever answer an ad break */
  private unavailable = false;

  constructor(private readonly c: H5AdsConfig) {}

  private get queue(): AdsByGoogle {
    const w = window as unknown as { adsbygoogle?: AdsByGoogle };
    return (w.adsbygoogle ??= [] as unknown as AdsByGoogle);
  }

  async init(opts: AdsInitOptions): Promise<void> {
    this.queue.requestNonPersonalizedAds = nonPersonalized(opts) ? 1 : 0;
    try {
      await loadScript(`https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(this.c.client)}`, this.c.document, {
        'data-ad-client': this.c.client,
        crossorigin: 'anonymous',
        ...(this.c.testMode ? { 'data-adbreak-test': 'on' } : {}),
        ...(opts.childDirected ? { 'data-tag-for-child-directed-treatment': '1' } : {}),
      });
    } catch (e) {
      this.unavailable = true;
      throw e;
    }
    this.queue.push({ preloadAdBreaks: 'on', sound: 'on', onReady: () => (this.ready = true) });
  }

  async isRewardedReady(_slot: AdSlot): Promise<boolean> {
    return this.ready;
  }

  showRewarded(slot: AdSlot, _nonce: string): Promise<RewardedResult> {
    const miss = (detail: string): RewardedResult => ({ completed: false, network: this.network, error: 'nofill', detail });
    // the tag never loaded (an ad blocker, no network): nothing will answer, so say so at once
    if (this.unavailable) return Promise.resolve(miss('unavailable'));
    let viewed = false;
    const result = (breakStatus: BreakStatus): RewardedResult =>
      viewed
        ? { completed: true, network: this.network }
        : { completed: false, network: this.network, error: breakStatus === 'dismissed' ? 'dismissed' : breakStatus === 'error' ? 'failed' : 'nofill', detail: breakStatus };
    return this.adBreak<RewardedResult>(
      (done, started, live) => ({
        type: 'reward',
        name: slot,
        beforeReward: (show) => {
          // a break the game already gave up on is never shown: the player was told there is no ad,
          // and a view now could not be rewarded
          if (!live()) return;
          started();
          show();
        },
        adViewed: () => (viewed = true),
        adBreakDone: ({ breakStatus }) => done(result(breakStatus)),
      }),
      () => (viewed ? { completed: true, network: this.network } : miss('timeout')),
    );
  }

  showInterstitial(): Promise<{ shown: boolean }> {
    if (this.unavailable) return Promise.resolve({ shown: false });
    return this.adBreak<{ shown: boolean }>(
      (done) => ({
        type: 'next',
        name: 'between-levels',
        adBreakDone: ({ breakStatus }) => done({ shown: breakStatus === 'viewed' || breakStatus === 'dismissed' }),
      }),
      () => ({ shown: false }),
    );
  }

  /**
   * Push one ad break and settle exactly once: on adBreakDone, or with `fallback()` when the break has
   * not started within H5_START_MS (a tag still loading, or an ad blocker's stub that swallows every
   * call) or a started break outlives H5_MAX_BREAK_MS. `live()` is false once it has settled. A break
   * the tag starts after that still pauses the game, and resumes it when it ends or times out.
   */
  private adBreak<T>(make: (done: (r: T) => void, started: () => void, live: () => boolean) => AdBreak, fallback: () => T): Promise<T> {
    return new Promise((resolve) => {
      let settled = false;
      let paused = false;
      let timer: ReturnType<typeof setTimeout> | undefined;
      const resume = () => {
        if (!paused) return;
        paused = false;
        this.c.onResume?.();
      };
      const done = (r: T) => {
        clearTimeout(timer);
        resume();
        if (settled) return;
        settled = true;
        resolve(r);
      };
      const started = () => {
        clearTimeout(timer);
        timer = setTimeout(() => done(fallback()), H5_MAX_BREAK_MS);
      };
      const ad = make(done, started, () => !settled);
      timer = setTimeout(() => done(fallback()), H5_START_MS);
      this.queue.push({
        ...ad,
        beforeAd: () => {
          started();
          paused = true;
          this.c.onPause?.();
        },
        afterAd: resume,
      });
    });
  }
}
